// JobPanels — the five child collections of a job, as five small panels.
//
// Each one is a list and a way to add to it. All the data wiring lives in
// jobResources.js, so a panel here is presentation plus the one or two rules
// that are specific to it. That is the whole point of the split: adding a
// sixth collection should be an entry in JOB_RESOURCES and a component this
// size, not another copy of react-query.
//
// Rules that ARE specific, read off the server rather than assumed:
//   · a worker may tick a subtask but not retitle one (403), so the title
//     input is only offered when `canEdit`
//   · moving work-list items needs jobs oversight (manage_all / manage_team,
//     which admin and manager hold by seed and anyone can be granted) and
//     cannot move a TICKED item, so the Move control and the list inside it
//     are both narrower than the panel around them (subtaskMove.js says why)
//   · a comment is editable by its author or an admin
//   · a prerequisite is removed by the id of the job depended ON
//   · history is read-only and newest-first

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckRow, FwButton, FwField, FwInput, FwTextarea, FwSelect, Notice, Skeleton, EmptyState, Pill, SectionLabel, Sheet } from '../../../ui/fw';
import { Icon } from '../../../ui';
import MediaViewer from './MediaViewer';
import { MEDIA_ACCEPT, isImageFile, imagesOf, fileIcon, fileLabel, fileHref, fmtBytes } from './mediaKinds';
import { useJobResource } from './jobResources';
import { statusLabel, statusTone } from '../jobFields';
import { post, serverSentence } from '../http';
import { canMoveSubtasks, movableSubtasks, validateMove, moveBody, moveTargets, describeMoved } from './subtaskMove';
import './JobPanels.css';

/**
 * The number for a panel heading — omitted entirely when the list did not
 * load.
 *
 * `useJobResource` hands back `rows: []` for a query that was refused, so a
 * bare `rows.length` prints "Comments (0)" over a 403 and "Waiting on (0)"
 * over a 500. The body below it does say the load failed, and that is not
 * enough: a sentence is visibly a claim and gets questioned, while a count
 * reads as arithmetic and is trusted. "Waiting on (0)" is the one that costs
 * something — nothing is holding this job up is a green light to start, and it
 * was measured from a request that never answered.
 */
export function panelCount(list, render = (n) => n) {
  return list.loadError ? undefined : render(list.rows.length);
}

/**
 * Ask before a delete. One tap used to delete a photo — and the server takes
 * the FILE off the disk with it (routes/media.js) — a work-list item or a
 * comment, from a trash icon beside the thing a thumb is scrolling past.
 * Classic asked every time; the rebuild did not. Returns the `ask` to wire to
 * a trash button and the sheet to render once in the panel.
 */
function useConfirmDelete(list) {
  const [pending, setPending] = useState(null); // { id, what, detail }
  const close = () => setPending(null);
  const confirm = async () => {
    const p = pending;
    await list.remove(p.id);
    // Closed either way: a refusal is shown by the panel's own error line.
    setPending(null);
  };
  const sheet = (
    <Sheet
      open={!!pending}
      title={pending ? `Delete ${pending.what}?` : ''}
      onClose={close}
      footer={(
        <div className="jp__confirm">
          <FwButton variant="ghost" onClick={close} disabled={list.busy}>Keep it</FwButton>
          <FwButton variant="stop" icon="trash" onClick={confirm} loading={list.busy} data-confirm="delete">Delete</FwButton>
        </div>
      )}
    >
      {pending ? <p>{pending.detail}</p> : null}
    </Sheet>
  );
  return { ask: (id, what, detail) => setPending({ id, what, detail }), sheet };
}

/** Shared frame: a heading, a count, an error line, and the body. */
function Panel({ title, count, error, onDismissError, children, actions }) {
  return (
    <section className="jp">
      <header className="jp__head">
        <SectionLabel>{title}{count === undefined ? '' : ` (${count})`}</SectionLabel>
        {actions ? <div className="jp__actions">{actions}</div> : null}
      </header>
      {error ? <Notice tone="stop" onDismiss={onDismissError}>{error}</Notice> : null}
      {children}
    </section>
  );
}

function Body({ list, empty, children }) {
  if (list.isLoading) return <Skeleton lines={3} />;
  if (list.loadError) {
    return <Notice tone="stop">{list.loadError.message || 'Could not load these.'}</Notice>;
  }
  if (!list.rows.length) return <EmptyState title={empty} />;
  return children;
}

const when = (ts) => {
  if (!ts) return '';
  const d = new Date(String(ts).replace(' ', 'T') + (String(ts).endsWith('Z') ? '' : 'Z'));
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
};

// ── Work list ───────────────────────────────────────────────────────────────

/**
 * Move unticked work-list items to another job, or to a new continuation job.
 *
 * Only the unticked ones are listed, because the handler's UPDATE carries
 * `AND completed = 0` — a ticked item put on this list would be one the person
 * watches fail to move with nothing said about it.
 */
function MoveSubtasksSheet({ open, onClose, jobId, rows, jobs, onMoved }) {
  const qc = useQueryClient();
  const movable = useMemo(() => movableSubtasks(rows), [rows]);
  const targets = useMemo(() => moveTargets(jobs, jobId), [jobs, jobId]);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState('existing');
  const [targetJobId, setTargetJobId] = useState('');
  const [newJobTitle, setNewJobTitle] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (id, next) => setPicked((prev) => (next ? [...prev, id] : prev.filter((x) => x !== id)));

  const submit = async () => {
    const args = { subtaskIds: picked, sourceJobId: jobId, mode, targetJobId, newJobTitle };
    const refusal = validateMove(args);
    if (refusal) { setError(refusal); return; }
    setBusy(true);
    setError('');
    try {
      const answer = await post('/subtasks/move', moveBody(args));
      // Both jobs changed, and a continuation job may have been created, so
      // the caller refreshes its own list rather than this panel guessing at
      // its query key.
      await qc.invalidateQueries({ queryKey: ['job'] });
      if (onMoved) onMoved(describeMoved(answer, picked.length));
      onClose();
    } catch (e) {
      // serverSentence, not describeError: the handler's own 403 names the
      // obstacle ("Only managers and admins can move subtasks") and
      // describeError would flatten it to a generic permission line.
      setError(serverSentence(e, 'Those items could not be moved.'));
    } finally {
      setBusy(false);
    }
  };

  // Nothing unticked: say so rather than opening an empty sheet with a
  // confirm button that cannot do anything.
  const body = movable.length === 0 ? (
    <Notice tone="warn">Everything on this work list is ticked off, and ticked items do not move.</Notice>
  ) : (
    <>
      <SectionLabel>Which items</SectionLabel>
      <ul className="jp__list">
        {movable.map((s) => (
          <li key={s.id} className="jp__row">
            <CheckRow
              checked={picked.includes(s.id)}
              label={s.title}
              disabled={busy}
              onToggle={(next) => toggle(s.id, next)}
            />
          </li>
        ))}
      </ul>
      <SectionLabel>Where to</SectionLabel>
      <FwField label="Destination" htmlFor="jp-move-mode">
        <FwSelect id="jp-move-mode" value={mode} onChange={(e) => { setMode(e.target.value); setError(''); }} disabled={busy}>
          <option value="existing">Another job</option>
          <option value="new">A new job</option>
        </FwSelect>
      </FwField>
      {mode === 'existing' ? (
        <FwField label="Job" htmlFor="jp-move-target" error={targets.length === 0 ? 'No other job to move these to.' : undefined}>
          <FwSelect id="jp-move-target" value={targetJobId} onChange={(e) => setTargetJobId(e.target.value)} disabled={busy || targets.length === 0}>
            <option value="">Choose a job</option>
            {targets.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </FwSelect>
        </FwField>
      ) : (
        <FwField label="New job name" htmlFor="jp-move-title">
          <FwInput
            id="jp-move-title"
            value={newJobTitle}
            onChange={(e) => setNewJobTitle(e.target.value)}
            placeholder="Henderson lot — finish Tuesday"
            maxLength={200}
            disabled={busy}
          />
        </FwField>
      )}
      <p className="jp__hint">
        {mode === 'new'
          ? 'The new job keeps this job\u2019s client, property and lead.'
          : 'Ticked items stay on this job.'}
      </p>
    </>
  );

  return (
    <Sheet
      open={open}
      title="Move work-list items"
      onClose={onClose}
      footer={(
        <>
          <FwButton variant="quiet" onClick={onClose} disabled={busy}>Cancel</FwButton>
          {movable.length > 0 ? (
            <FwButton variant="go" onClick={submit} loading={busy} aria-label="Move selected items">
              Move{picked.length ? ` ${picked.length}` : ''}
            </FwButton>
          ) : null}
        </>
      )}
    >
      {error ? <Notice tone="stop" onDismiss={() => setError('')}>{error}</Notice> : null}
      {body}
    </Sheet>
  );
}

export function SubtasksPanel({ jobId, canEdit = false, canDelete = false, user, jobs = [], onMoved }) {
  const list = useJobResource('subtasks', jobId);
  const removal = useConfirmDelete(list);
  const [title, setTitle] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moved, setMoved] = useState('');
  // Takes the USER, not the role label. POST /api/subtasks/move asks for
  // jobs.manage_all / manage_team as of 2026-09-07, so a role string here
  // would hide the button from somebody the server would obey.
  const canMove = canMoveSubtasks(user);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (await list.create({ title: title.trim() })) setTitle('');
  };

  const done = list.rows.filter((s) => s.completed).length;

  return (
    <Panel
      title="Work list"
      count={panelCount(list, (n) => (n ? `${done} of ${n}` : undefined))}
      error={list.error}
      onDismissError={list.clearError}
      actions={canMove && list.rows.length ? (
        <FwButton variant="quiet" icon="arrowRight" onClick={() => setMoveOpen(true)}>Move</FwButton>
      ) : null}
    >
      {moved ? <Notice tone="ok" onDismiss={() => setMoved('')}>{moved}</Notice> : null}
      <Body list={list} empty="No work list yet.">
        <ul className="jp__list">
          {list.rows.map((s) => (
            <li key={s.id} className="jp__row">
              <CheckRow
                checked={!!s.completed}
                label={s.title}
                meta={s.completed && s.completed_by_name ? `${s.completed_by_name} · ${when(s.completed_at)}` : undefined}
                disabled={list.busy}
                onToggle={(next) => list.update(s.id, { completed: next })}
              />
              {canDelete ? (
                <FwButton variant="quiet" icon="trash" aria-label={`Remove ${s.title}`} onClick={() => removal.ask(s.id, 'this item', `"${s.title}" comes off the work list.`)} disabled={list.busy} />
              ) : null}
            </li>
          ))}
        </ul>
      </Body>
      {canEdit ? (
        <form className="jp__add" onSubmit={add}>
          <FwField label="Add an item" htmlFor="jp-subtask-title">
            <FwInput id="jp-subtask-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Edge the beds" maxLength={200} />
          </FwField>
          <FwButton type="submit" variant="ghost" icon="plus" loading={list.busy}>Add</FwButton>
        </form>
      ) : null}
      {removal.sheet}
      {canMove ? (
        <MoveSubtasksSheet
          open={moveOpen}
          onClose={() => setMoveOpen(false)}
          jobId={jobId}
          rows={list.rows}
          jobs={jobs}
          onMoved={(sentence) => { setMoved(sentence); if (onMoved) onMoved(sentence); }}
        />
      ) : null}
    </Panel>
  );
}

// ── Comments ────────────────────────────────────────────────────────────────

export function CommentsPanel({ jobId, currentUserId, role }) {
  const list = useJobResource('comments', jobId);
  const removal = useConfirmDelete(list);
  const [text, setText] = useState('');

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (await list.create({ comment: text.trim() })) setText('');
  };

  // The server lets the author or an admin edit, and the author, a manager or
  // an admin delete. Offering a control the server will refuse is worse than
  // not offering it.
  const mine = (c) => String(c.user_id) === String(currentUserId);
  const canDelete = (c) => mine(c) || role === 'admin' || role === 'manager';

  return (
    <Panel title="Comments" count={panelCount(list)} error={list.error} onDismissError={list.clearError}>
      <Body list={list} empty="No comments yet.">
        <ul className="jp__list">
          {list.rows.map((c) => (
            <li key={c.id} className="jp__comment">
              <div className="jp__comment-head">
                <strong>{c.user_name || 'Someone'}</strong>
                <span className="jp__when">{when(c.created_at)}</span>
                {canDelete(c) ? (
                  <FwButton variant="quiet" icon="trash" aria-label="Delete comment" onClick={() => removal.ask(c.id, 'this comment', `${c.user_name || 'Someone'}'s comment is removed for everyone on the job.`)} disabled={list.busy} />
                ) : null}
              </div>
              <p className="jp__comment-body">{c.comment}</p>
            </li>
          ))}
        </ul>
      </Body>
      <form className="jp__add jp__add--stacked" onSubmit={add}>
        <FwField label="Add a comment" htmlFor="jp-comment">
          <FwTextarea id="jp-comment" rows={2} value={text} onChange={(e) => setText(e.target.value)} />
        </FwField>
        <FwButton type="submit" variant="ghost" icon="plus" loading={list.busy}>Post</FwButton>
      </form>
      {removal.sheet}
    </Panel>
  );
}

// ── Photos and files ─────────────────────────────────────────────

/**
 * Everything attached to a job — photos AND documents — with a viewer.
 *
 * Two parity gaps meet here (docs/PARITY_2026-09-08.md):
 *
 *   §6  the input said `accept="image/*"` and every row was drawn as an
 *       unconditional `<img>`, so a PDF permit could not be added and one
 *       added before the rebuild showed as a broken image. The server has
 *       accepted documents the whole time (server/routes/media.js), and
 *       office/insight/Media.js has offered filters for them; only this panel
 *       and the phone were narrow. `mediaKinds.js` is the declaration, and
 *       mediaKinds.test.js checks it against that route file.
 *   §16 a thumbnail was an `<a target="_blank">` — the browser's own image
 *       view, with no paging and no download. Tapping one opens MediaViewer
 *       now, which pages the IMAGES of the list and downloads the one on
 *       screen. A document stays a link, because there is nothing to page.
 *
 * `viewing` holds the id, not the index: an index goes stale the moment a
 * delete or a refetch reorders the list, and pointing a viewer at the wrong
 * photo is worse than not opening one.
 */
export function PhotosPanel({ jobId, canEdit = false }) {
  const list = useJobResource('photos', jobId);
  const removal = useConfirmDelete(list);
  const [caption, setCaption] = useState({});
  const [viewing, setViewing] = useState(null);
  const [progress, setProgress] = useState(null);

  /**
   * A selection of any size. `uploadFiles` chunks it into as many requests as
   * it takes (../photoBatches.js), so thirty photos of a property is five
   * requests rather than one refusal — which is what a crew member met when
   * they reported "can't upload more than 10 at a time".
   *
   * The FileList is copied BEFORE the input is cleared: `e.target.files` is
   * live-bound to the element, so clearing it first would hand the uploader an
   * empty list. Clearing it at all is what lets the same file be picked twice
   * in a row.
   *
   * `progress` is not decoration. Thirty photos over a yard connection is
   * minutes of a spinner, and a button that says only "Uploading…" for that
   * long is one a crew member taps again or walks away from.
   */
  const onFiles = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    input.value = '';           // so the same file can be picked again
    if (!files.length) return;
    setProgress({ sent: 0, total: files.length });
    try {
      await list.uploadFiles(files, { onProgress: setProgress });
    } finally {
      setProgress(null);
    }
  };

  // "Uploading 12 of 30…" once there is more than one request in it; a single
  // photo does not need a count.
  const uploadLabel = progress && progress.total > 1
    ? `Uploading ${progress.sent} of ${progress.total}…`
    : 'Uploading…';

  const images = imagesOf(list.rows);
  const viewIndex = images.findIndex((m) => String(m.id) === String(viewing));

  return (
    <Panel
      title="Photos & files"
      count={panelCount(list)}
      error={list.error}
      onDismissError={list.clearError}
      actions={canEdit ? (
        <label className="jp__file">
          <input type="file" accept={MEDIA_ACCEPT} multiple onChange={onFiles} disabled={list.busy} aria-label="Add photos or files" />
          <span>{list.busy ? uploadLabel : 'Add photos or files'}</span>
        </label>
      ) : null}
    >
      <Body list={list} empty="No photos or files yet.">
        <ul className="jp__grid">
          {list.rows.map((m) => {
            const name = fileLabel(m);
            const href = fileHref(m);
            return (
              <li key={m.id} className="jp__photo">
                {isImageFile(m) ? (
                  <button
                    type="button"
                    className="jp__thumb"
                    onClick={() => setViewing(m.id)}
                    aria-label={`Open ${name}`}
                  >
                    <img src={href} alt={m.caption || name} loading="lazy" />
                  </button>
                ) : (
                  /* A document has no thumbnail, so the glyph and the NAME are
                     the whole of what identifies it — the same shape
                     office/insight/Media.js draws. Opening it is a plain link:
                     a browser knows what to do with a PDF and this app does
                     not need to. */
                  <a className="jp__doc" href={href} target="_blank" rel="noreferrer" aria-label={`Open ${name}`}>
                    <span className="jp__doc-glyph" aria-hidden="true"><Icon name={fileIcon(m)} /></span>
                    <span className="jp__doc-name">{name}</span>
                  </a>
                )}
                <div className="jp__photo-meta">
                  <span className="jp__when">
                    {[m.uploaded_by_name, when(m.created_at), fmtBytes(m.file_size)]
                      .filter((x) => x && x !== '—').join(' · ')}
                  </span>
                  <a className="jp__download" href={href} download={name} aria-label={`Download ${name}`}>
                    <Icon name="download" /> <span>Download</span>
                  </a>
                  {canEdit ? (
                    <>
                      <FwInput
                        aria-label={`Caption for ${name}`}
                        value={caption[m.id] === undefined ? (m.caption || '') : caption[m.id]}
                        onChange={(e) => setCaption((c) => ({ ...c, [m.id]: e.target.value }))}
                        onBlur={(e) => {
                          const next = e.target.value;
                          if (next !== (m.caption || '')) list.update(m.id, { caption: next });
                        }}
                        placeholder="Add a caption"
                      />
                      <FwButton variant="quiet" icon="trash" aria-label={`Delete ${name}`} onClick={() => removal.ask(m.id, name, 'The file is deleted from the job and from the server. It cannot be brought back.')} disabled={list.busy} />
                    </>
                  ) : m.caption ? <span>{m.caption}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </Body>
      {viewIndex >= 0 ? (
        <MediaViewer
          items={images}
          index={viewIndex}
          onIndex={(i) => setViewing(images[i] && images[i].id)}
          onClose={() => setViewing(null)}
        />
      ) : null}
      {removal.sheet}
    </Panel>
  );
}

// ── Prerequisites ───────────────────────────────────────────────────────────

export function DependenciesPanel({ jobId, jobs = [], canEdit = false }) {
  const list = useJobResource('dependencies', jobId);
  const [pick, setPick] = useState('');

  const already = useMemo(
    () => new Set(list.rows.map((d) => String(d.depends_on_job_id))),
    [list.rows],
  );
  const options = jobs.filter((j) => String(j.id) !== String(jobId) && !already.has(String(j.id)));

  const add = async (e) => {
    e.preventDefault();
    if (!pick) return;
    if (await list.create({ depends_on_job_id: Number(pick) })) setPick('');
  };

  return (
    <Panel title="Waiting on" count={panelCount(list)} error={list.error} onDismissError={list.clearError}>
      <Body list={list} empty="Nothing is holding this job up.">
        <ul className="jp__list">
          {list.rows.map((d) => (
            <li key={d.depends_on_job_id} className="jp__row jp__row--dep">
              <span className="jp__dep-title">{d.title}</span>
              {/*
                Two bugs in one element. It printed the RAW token — a
                prerequisite read "todo", lowercase, in a pill — which is the
                exact case statusLabel exists for (jobFields.js: printing the
                raw token once put "BLOCKED" on a card in an uppercase pill).
                And its own done/not-done tone made every unfinished
                prerequisite the same amber, so "active" and "todo" were
                indistinguishable here even though they answer different
                questions for somebody deciding whether to go.
              */}
              <Pill tone={statusTone(d.status)}>{statusLabel(d.status)}</Pill>
              <span className="jp__when">{d.assigned_to_name || 'Unassigned'}</span>
              {canEdit ? (
                // The path takes the id of the job depended ON, not a row id.
                <FwButton variant="quiet" icon="x" aria-label={`Stop waiting on ${d.title}`} onClick={() => list.remove(d.depends_on_job_id)} disabled={list.busy} />
              ) : null}
            </li>
          ))}
        </ul>
      </Body>
      {canEdit && options.length ? (
        <form className="jp__add" onSubmit={add}>
          <FwField label="This job cannot start until…" htmlFor="jp-dep">
            <FwSelect id="jp-dep" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Pick a job</option>
              {options.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
            </FwSelect>
          </FwField>
          <FwButton type="submit" variant="ghost" icon="plus" loading={list.busy} disabled={!pick}>Add</FwButton>
        </form>
      ) : null}
    </Panel>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

export function ActivityPanel({ jobId }) {
  const list = useJobResource('activity', jobId);
  return (
    <Panel title="History" count={panelCount(list)}>
      <Body list={list} empty="Nothing recorded yet.">
        <ol className="jp__list jp__list--trail">
          {list.rows.map((a) => (
            <li key={a.id} className="jp__event">
              <span className="jp__event-what">{a.description}</span>
              <span className="jp__when">{a.user_name} · {when(a.created_at)}</span>
            </li>
          ))}
        </ol>
      </Body>
    </Panel>
  );
}
